const MONTHS_COUNT = 12;
const YEARS_RANGE = 7;
const YEARS_OFFSET = 3;
const VIETNAM_LOCALE = 'vi-VN' as const;
const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY' as const;
const DATE_TIME_DISPLAY_FORMAT = 'DD/MM/YYYY HH:mm:ss' as const;

type DateLike = string | number | Date | null | undefined;

const parseDdMmYyyy = (value: string): Date | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const d = new Date(yyyy, mm - 1, dd);
  // Validate (tránh 31/02)
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
};

/**
 * Generate array of months (1-12)
 * Memoized to avoid recreating on every call
 */
const MONTHS_ARRAY = Array.from({ length: MONTHS_COUNT }, (_, i) => i + 1) as readonly number[];

export const getMonths = (): readonly number[] => {
  return MONTHS_ARRAY;
};

/**
 * Generate array of years (current year ± 3 years)
 */
export const getYears = (): readonly number[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: YEARS_RANGE }, (_, i) => currentYear - YEARS_OFFSET + i) as readonly number[];
};

/**
 * Chuẩn hoá format ngày hiển thị: DD/MM/YYYY
 * - Nhận Date / timestamp / ISO string
 * - Trả về '' nếu invalid/empty
 */
export const formatDateDisplay = (value: DateLike): string => {
  if (value === null || value === undefined || value === '') return '';
  const date =
    typeof value === 'string'
      ? parseDdMmYyyy(value) ?? new Date(value)
      : value instanceof Date
      ? value
      : new Date(value);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Chuẩn hoá format ngày-giờ hiển thị: DD/MM/YYYY HH:mm:ss
 */
export const formatDateTimeDisplay = (value: DateLike): string => {
  if (value === null || value === undefined || value === '') return '';
  const date =
    typeof value === 'string'
      ? parseDdMmYyyy(value) ?? new Date(value)
      : value instanceof Date
      ? value
      : new Date(value);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hh}:${mm}:${ss}`;
};

export const getCurrentMonth = (): number => new Date().getMonth() + 1;
export const getCurrentYear = (): number => new Date().getFullYear();

/**
 * Lấy ngày hiện tại theo locale vi-VN (fallback cho các màn hình đang lưu startDate dạng string).
 * Ưu tiên dùng `formatDateDisplay(new Date())` nếu muốn DD/MM/YYYY cố định.
 */
export const getTodayLocaleDateString = (): string => {
  return new Date().toLocaleDateString(VIETNAM_LOCALE);
};

// Export constants để tránh hard-code rải rác (tuỳ nhu cầu dùng trong pages/components)
export const DATE_FORMATS = {
  displayDate: DATE_DISPLAY_FORMAT,
  displayDateTime: DATE_TIME_DISPLAY_FORMAT,
  locale: VIETNAM_LOCALE,
} as const;

